import React, { useState, useEffect } from "react";
import { useJobsListQuery, useJobStatusQuery } from "@/api/betting";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { getJobStatusIndicator } from "./StatusBadges";
import { RefreshCw, List, PieChart } from "lucide-react";

interface JobsPanelProps {
  personaId?: string;
  limit?: number;
  showHeader?: boolean;
  className?: string;
}

// Job types
interface JobSummary {
  job_id: string;
  status: string;
  persona_id: string;
  type: string;
  results_summary?: JobResultsSummary;
}

// Define proper types for job result summaries
interface ValidateJobResults {
  total?: number;
  valid?: number;
  wins?: number;
  losses?: number;
  pending?: number;
}

interface BackfillJobResults {
  processed?: number;
  extracted?: number;
  validated?: number;
  limit?: number;
}

// Union type for job results
type JobResultsSummary = ValidateJobResults | BackfillJobResults;

const JobsPanel: React.FC<JobsPanelProps> = ({
  personaId,
  limit = 5,
  showHeader = true,
  className = "",
}) => {
  const [activeTab, setActiveTab] = useState<"running" | "completed" | "all">(
    "running",
  );
  const [activeJobId, setActiveJobId] = useState<string | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Handle manual refresh
  const handleRefresh = () => {
    setRefreshTrigger((prev) => prev + 1);
  };

  // Determine status filter based on active tab
  const statusFilter =
    activeTab === "running"
      ? "in_progress,queued"
      : activeTab === "completed"
        ? "completed,failed"
        : undefined;

  // Fetch jobs data
  const {
    data: jobsData,
    isLoading,
    refetch,
  } = useJobsListQuery(personaId, statusFilter, limit, true);

  // Fetch details for active job
  const { data: activeJobData, isLoading: isJobLoading } = useJobStatusQuery(
    activeJobId || "",
    !!activeJobId,
  );

  // Refetch when refresh trigger changes
  useEffect(() => {
    refetch();
  }, [refetch, refreshTrigger]);

  // Auto select first job if none selected and jobs available
  useEffect(() => {
    if (!activeJobId && jobsData?.jobs && jobsData.jobs.length > 0) {
      setActiveJobId(jobsData.jobs[0].job_id);
    }
  }, [activeJobId, jobsData]);

  // Format job type for display
  const formatJobType = (jobType: string) => {
    switch (jobType) {
      case "validate":
        return "Validation";
      case "backfill":
        return "Backfill";
      default:
        return jobType;
    }
  };

  // Calculate progress percentage (for completed jobs)
  const calculateProgress = (job: JobSummary): number => {
    if (!job.results_summary) return 0;

    if (job.type === "validate") {
      const results = job.results_summary as ValidateJobResults;
      const total = results.total || 0;
      const processed =
        (results.valid || 0) +
        (results.wins || 0) +
        (results.losses || 0) +
        (results.pending || 0);

      return total > 0 ? Math.min(100, (processed / total) * 100) : 0;
    }

    if (job.type === "backfill") {
      const results = job.results_summary as BackfillJobResults;
      const processed = results.processed || 0;
      const limit = results.limit || processed || 1;

      return limit > 0 ? Math.min(100, (processed / limit) * 100) : 0;
    }

    return 0;
  };

  return (
    <Card className={`bg-black/60 backdrop-blur-sm ${className}`}>
      {showHeader && (
        <CardHeader className="p-3">
          <div className="flex justify-between items-center">
            <CardTitle className="text-base font-medium">Jobs</CardTitle>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={handleRefresh}
            >
              <RefreshCw className="h-4 w-4 text-muted-foreground" />
            </Button>
          </div>
        </CardHeader>
      )}

      <Tabs
        value={activeTab}
        onValueChange={(value) => setActiveTab(value as any)}
        className="w-full"
      >
        <TabsList className="grid grid-cols-3 mb-2 w-full bg-black/60">
          <TabsTrigger value="running" className="text-xs py-1">
            Running
          </TabsTrigger>
          <TabsTrigger value="completed" className="text-xs py-1">
            Completed
          </TabsTrigger>
          <TabsTrigger value="all" className="text-xs py-1">
            All
          </TabsTrigger>
        </TabsList>
      </Tabs>

      <CardContent className="px-3 pb-3 pt-0">
        {isLoading ? (
          <div className="space-y-2">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="flex items-center gap-2 py-1">
                <Skeleton className="h-4 w-4 rounded-full" />
                <Skeleton className="h-4 w-full" />
              </div>
            ))}
          </div>
        ) : jobsData?.jobs && jobsData.jobs.length > 0 ? (
          <div className="grid grid-cols-2 gap-3">
            {/* Jobs List */}
            <div className="space-y-1">
              {jobsData.jobs.map((job) => (
                <div
                  key={job.job_id}
                  className={`flex flex-col p-2 rounded cursor-pointer text-xs transition-colors ${
                    activeJobId === job.job_id
                      ? "bg-blue-950/30 border border-blue-900"
                      : "hover:bg-zinc-800/40 border border-transparent"
                  }`}
                  onClick={() => setActiveJobId(job.job_id)}
                >
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-1.5">
                      {job.type === "validate" ? (
                        <PieChart className="h-3 w-3 text-blue-400" />
                      ) : (
                        <List className="h-3 w-3 text-amber-400" />
                      )}
                      <span className="font-medium">
                        {formatJobType(job.type)}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      {job.status === "completed" || job.status === "failed" ? (
                        <Badge
                          className={`text-[10px] ${
                            job.status === "completed"
                              ? "bg-green-900/30 text-green-500 border-green-800"
                              : "bg-red-900/30 text-red-500 border-red-800"
                          }`}
                        >
                          {job.status}
                        </Badge>
                      ) : (
                        <Badge className="bg-blue-900/30 text-blue-400 border-blue-800 text-[10px] flex items-center gap-1">
                          <RefreshCw className="h-2 w-2 animate-spin" />
                          <span>{job.status}</span>
                        </Badge>
                      )}
                    </div>
                  </div>

                  {/* Progress indicator for completed jobs */}
                  {job.status === "completed" && job.results_summary && (
                    <div className="mt-2">
                      <Progress
                        value={calculateProgress(job)}
                        className="h-1 bg-zinc-800"
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Job Details */}
            <div className="bg-black/40 border border-zinc-800 rounded p-2">
              {activeJobId && activeJobData ? (
                <div className="space-y-2 text-xs">
                  <div className="flex justify-between">
                    <span className="text-zinc-400">Status:</span>
                    <span>{getJobStatusIndicator(activeJobData.status)}</span>
                  </div>

                  <div className="flex justify-between">
                    <span className="text-zinc-400">Type:</span>
                    <span className="font-medium">
                      {formatJobType(activeJobId.split("_")[0])}
                    </span>
                  </div>

                  <div className="flex justify-between">
                    <span className="text-zinc-400">Agent:</span>
                    <span className="font-medium truncate max-w-24 text-right">
                      {activeJobData.persona_id}
                    </span>
                  </div>

                  {activeJobData.results && (
                    <div className="pt-2 border-t border-zinc-800 mt-2">
                      <div className="text-zinc-400 mb-1">Results:</div>
                      {activeJobData.status === "completed" ? (
                        <div className="space-y-1">
                          {/* Format based on job type */}
                          {activeJobId.startsWith("validate") && (
                            <>
                              <div className="flex justify-between">
                                <span>Validated:</span>
                                <span className="font-medium">
                                  {(activeJobData.results as ValidateJobResults)
                                    .valid || 0}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span>Wins:</span>
                                <span className="font-medium text-green-500">
                                  {(activeJobData.results as ValidateJobResults)
                                    .wins || 0}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span>Losses:</span>
                                <span className="font-medium text-red-500">
                                  {(activeJobData.results as ValidateJobResults)
                                    .losses || 0}
                                </span>
                              </div>
                            </>
                          )}

                          {activeJobId.startsWith("backfill") && (
                            <>
                              <div className="flex justify-between">
                                <span>Processed:</span>
                                <span className="font-medium">
                                  {(activeJobData.results as BackfillJobResults)
                                    .processed || 0}{" "}
                                  messages
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span>Extracted:</span>
                                <span className="font-medium">
                                  {(activeJobData.results as BackfillJobResults)
                                    .extracted || 0}{" "}
                                  bets
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span>Validated:</span>
                                <span className="font-medium">
                                  {(activeJobData.results as BackfillJobResults)
                                    .validated || 0}{" "}
                                  bets
                                </span>
                              </div>
                            </>
                          )}
                        </div>
                      ) : (
                        <div className="italic text-zinc-500">
                          Job is still running...
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="h-full flex items-center justify-center text-zinc-500 text-xs">
                  {isJobLoading ? (
                    <div className="flex items-center gap-2">
                      <RefreshCw className="h-3 w-3 animate-spin" />
                      <span>Loading job details...</span>
                    </div>
                  ) : (
                    <span>Select a job to see details</span>
                  )}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="text-center py-4 text-zinc-500 text-xs">
            No {activeTab !== "all" ? activeTab : ""} jobs found
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default JobsPanel;
